/**
 * Attaching a document to a live hearing (PRD §6.3).
 *
 * Three roads to the same place: a paperclip beside the composer that opens a
 * small sheet, a drop anywhere over the thread, and a paste. The sheet is the
 * accessible one — a drop zone that is the only way in is not a way in at all —
 * so the overlay and the paste are accelerants and never the road.
 *
 * Everything here is shape and local interaction: what a chip reads as, what a
 * refusal says, whether a drag is over the column. The upload itself belongs to
 * the screen that owns the knock (`started.tsx`), which is the only thing that
 * knows there is a knock to attach to.
 */
import { useCallback, useEffect, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_EXTENSIONS,
  ATTACHMENT_MAX_BYTES,
  attachmentUrl,
  isAudioAttachment,
  type Attachment,
  type AttachmentFailureCode,
} from "../../lib/api";
import { SenderBubble } from "./chat";
import { AudioBubble, formatDuration } from "./audio-bubble";
import type { MenuTarget } from "./bubble-menu";

/** The paperclip's name, and the only accessible way to attach anything. */
export const ATTACH_LABEL = "Attach a document";

/** The chip's dismiss, which is a real button because it does a real thing. */
export const REMOVE_LABEL = "Remove attachment";

export const DROP_COPY = "Drop it here";

/** What a drop zone will take, said in the zone rather than after the refusal. */
export const DROP_TYPES = "PDF, DOCX, PPTX, TXT, MD, PNG or JPG, up to 25 MB";

/**
 * A HEIC photo, refused before it is sent (ruling R-0925-2 (4)): Nob reads an
 * image by one vision call, and that call takes PNG and JPEG.
 */
export const HEIC_REFUSAL =
  "Nob can't read HEIC photos. Send it as a PNG or a JPEG — a screenshot of it works.";

/** v1 takes one file at a time, and says so rather than silently dropping one. */
export const ONE_AT_A_TIME = "One at a time. Nob is still taking the first file.";

/** What the sender is told while the bytes are going up. */
export function uploadingLine(filename: string): string {
  return `Sending ${filename}.`;
}

/**
 * What a refusal says, per code (§6.3). One sentence each, dry and in the
 * second person — a sender who sent a 40 MB file and a sender who sent a `.zip`
 * have different problems and are told so.
 *
 * `null` means the code is not the thread's to answer: the session is gone, or
 * the knock is, and the caller has somewhere else to be.
 */
export function attachmentFailureLine(code: AttachmentFailureCode): string | null {
  switch (code) {
    case "ATTACHMENT_TOO_LARGE":
      return "That file is over 25 MB. Nob will not read it.";
    case "ATTACHMENT_UNSUPPORTED_TYPE":
      return "Nob reads PDF, DOCX, PPTX, TXT, MD, PNG and JPG. That was none of them.";
    case "ATTACHMENT_HEIC":
      return HEIC_REFUSAL;
    // Never explain the scanner, or name it (ruling R-0926-1 (2)). The file is
    // refused, and the sender is told that and nothing else.
    case "ATTACHMENT_QUARANTINED":
      return "This file can't be accepted.";
    // The one refusal that is nobody's fault, so the one that says to retry.
    case "ATTACHMENT_SCAN_UNAVAILABLE":
      return "Nob couldn't check that file just now. Send it again in a minute.";
    case "KNOCK_NOT_ACTIVE":
      return "This hearing is over.";
    case "VALIDATION_ERROR":
      return "Nob could not read that file.";
    case "UNAUTHENTICATED":
    case "KNOCK_NOT_FOUND":
      return null;
    case "UNKNOWN":
      return "Nob is not answering. Try that again.";
  }
}

/**
 * The refusal a file earns before it is ever sent. Both limits are the
 * server's; checking them here is not a second opinion, it is not making
 * somebody wait for a 25 MB round trip to be told about 25 MB.
 */
export function localRefusal(file: File): AttachmentFailureCode | null {
  const at = file.name.lastIndexOf(".");
  const ext = at === -1 ? "" : file.name.slice(at + 1).toLowerCase();
  // An iPhone photo, by its name or by what the picker says it is. Its own
  // sentence, because "none of them" leaves the sender with no idea what to do.
  if (ext === "heic" || ext === "heif" || /^image\/hei[cf]/i.test(file.type)) {
    return "ATTACHMENT_HEIC";
  }
  if (!(ATTACHMENT_EXTENSIONS as readonly string[]).includes(ext)) {
    return "ATTACHMENT_UNSUPPORTED_TYPE";
  }
  if (file.size > ATTACHMENT_MAX_BYTES) return "ATTACHMENT_TOO_LARGE";
  return null;
}

/** `2,516,582` → `"2.4 MB"`. A size is context, so it is never more precise than that. */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1000) return `${Math.round(bytes)} B`;
  const kb = bytes / 1024;
  const [value, unit] = kb < 1000 ? [kb, "KB"] : [kb / 1024, "MB"];
  const rounded = Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} ${unit}`;
}

/** `"deck.pdf · 12 pages"`, or just the name when nobody counted the pages. */
export function attachmentLine(attachment: Attachment): string {
  const pages = attachment.page_count;
  if (!pages || pages <= 0) return attachment.filename;
  return `${attachment.filename} · ${pages} page${pages === 1 ? "" : "s"}`;
}

const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70 " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-bg";

function Paperclip() {
  return (
    <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" aria-hidden="true">
      <path
        d="M13.5 7.5 8.2 12.8a1.9 1.9 0 0 0 2.7 2.7l5.6-5.6a3.4 3.4 0 0 0-4.8-4.8L6 10.8a4.9 4.9 0 0 0 6.9 6.9"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Document({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" className={className} fill="none" aria-hidden="true">
      <path
        d="M11.5 2.5H6a1.5 1.5 0 0 0-1.5 1.5v12A1.5 1.5 0 0 0 6 17.5h8a1.5 1.5 0 0 0 1.5-1.5V6.5zm0 0v4h4"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Cross() {
  return (
    <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
      <path d="M6 6l8 8M14 6l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/**
 * The paperclip, and the file input it drives. The input is hidden from
 * everybody — the button carries the name and the focus — because two things
 * called "Attach a document" is one thing too many for anyone listening.
 */
export const ATTACH_CAMERA = "Camera";
export const ATTACH_LIBRARY = "Photo or file";

/**
 * The attach affordance, as a small sheet rather than one hidden file input.
 *
 * A single `<input type="file">` is one answer to a question phones answer
 * three ways: the camera, the photo library, and the file system. On a phone
 * the browser's own picker for a bare input is a list of app names with no
 * indication that a photo of a page would do; naming the three makes the
 * commonest attachment — a photograph of a document — an obvious move rather
 * than a discovered one.
 *
 * The `capture` attribute is what routes the first row to the camera. Desktop
 * browsers ignore it and open the ordinary picker, which is the right
 * behaviour: nothing is offered that cannot be done, it simply lands in the
 * same place.
 *
 * The prototype's version of this sheet also carried "Connectors" and "Skills"
 * rows — an X account, a standing card, three shortcuts into other parts of the
 * app. None of those exist as things a knock can carry, so none of them are
 * here: an attach sheet that offered to attach a standing card would be an
 * offer the server refuses.
 */
export function AttachButton({
  onFile,
  disabled = false,
}: {
  onFile: (file: File) => void;
  disabled?: boolean;
}) {
  const camera = useRef<HTMLInputElement>(null);
  const library = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const shut = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener("click", shut);
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("click", shut);
      window.removeEventListener("keydown", key);
    };
  }, [open]);

  const take = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // The value is cleared so the same file picked twice is two picks.
    event.target.value = "";
    setOpen(false);
    if (file) onFile(file);
  };

  return (
    <div ref={box} className="relative">
      <input
        ref={camera}
        type="file"
        accept="image/*"
        capture="environment"
        tabIndex={-1}
        aria-hidden="true"
        data-testid="attach-camera"
        className="sr-only"
        onChange={take}
      />
      <input
        ref={library}
        type="file"
        accept={ATTACHMENT_ACCEPT}
        tabIndex={-1}
        aria-hidden="true"
        data-testid="attach-input"
        className="sr-only"
        onChange={take}
      />
      <button
        type="button"
        aria-label={ATTACH_LABEL}
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((on) => !on)}
        className={`flex h-11 w-9 shrink-0 items-center justify-center rounded-full text-faint
                    transition-colors duration-75 hover:text-text-soft
                    disabled:pointer-events-none disabled:opacity-50 ${FOCUS}`}
      >
        <Paperclip />
      </button>

      {open && (
        <div
          role="menu"
          aria-label={ATTACH_LABEL}
          className="absolute bottom-12 left-0 z-30 w-52 overflow-hidden rounded-lg border
                     border-border bg-bg shadow-lg"
        >
          <AttachRow label={ATTACH_CAMERA} onClick={() => camera.current?.click()} />
          <AttachRow label={ATTACH_LIBRARY} onClick={() => library.current?.click()} />
          <p className="border-t border-border-soft px-3 py-2 text-xs leading-snug text-fainter">
            {DROP_TYPES}
          </p>
        </div>
      )}
    </div>
  );
}

function AttachRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`block w-full border-b border-border-soft px-3 py-2.5 text-left text-sm
                  transition-colors duration-75 last:border-b-0 hover:bg-hover ${FOCUS}`}
    >
      {label}
    </button>
  );
}

export function PendingChip({
  filename,
  size,
  uploading,
  onDismiss,
}: {
  filename: string;
  size: number;
  uploading: boolean;
  onDismiss: () => void;
}) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <span
        className="flex min-w-0 max-w-full items-center gap-2 rounded-xl border border-border
                   bg-surface/70 py-1.5 pr-1.5 pl-3"
      >
        <Document className="h-4 w-4 shrink-0 text-faint" />
        <span className="min-w-0 truncate text-sm text-text-soft">{filename}</span>
        <span className="shrink-0 font-mono text-xs tabular-nums text-faint">
          {formatFileSize(size)}
        </span>
        <button
          type="button"
          aria-label={REMOVE_LABEL}
          onClick={onDismiss}
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-faint
                      transition-colors duration-75 hover:text-text-soft ${FOCUS}`}
        >
          <Cross />
        </button>
      </span>

      {/* Indeterminate on purpose: a multipart POST has no progress to report,
          and a bar that invents one is a bar that lies. */}
      {uploading && (
        <span
          data-testid="attach-progress"
          className="nob-breathe h-0.5 w-16 shrink-0 rounded-full bg-accent/70"
          aria-hidden="true"
        />
      )}

      <span className="sr-only" aria-live="polite">
        {uploading ? uploadingLine(filename) : ""}
      </span>
    </div>
  );
}

/** What a voice note is called for anyone not looking at the thread. */
export function voiceNoteLabel(attachment: Attachment): string {
  const length = attachment.duration_ms ? ` · ${formatDuration(attachment.duration_ms)}` : "";
  return `Voice note${length}`;
}

/**
 * The words under a player (ruling R-0905-4): what the transcriber heard, in
 * small type, subordinate to the clip. It is shown wherever the bubble is —
 * the sender's thread, the owner's own thread, the recipient's card — because
 * both sides read the same words the doorman was given. Nothing is drawn when
 * there is no transcript: a player with no caption is a note nobody could
 * hear, and that is a fact rather than a gap to paper over.
 */
export function Transcript({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <p
      data-testid="voice-transcript"
      className="mt-1.5 max-w-prose whitespace-pre-wrap text-xs leading-snug text-faint"
    >
      {text}
    </p>
  );
}

/**
 * The landed attachment, in the sender's own voice: their file, on their side.
 *
 * A recording gets a player, and its transcript under it; everything else gets
 * its name and its page count. That split is the whole of the Lightbox seam
 * for now — an image is still a filename here, deliberately, because opening
 * one is a viewer this wave did not build and a half-built one is worse than
 * none.
 */
export function AttachmentBubble({
  attachment,
  onMenu,
}: {
  attachment: Attachment;
  onMenu?: (target: MenuTarget) => void;
}) {
  if (isAudioAttachment(attachment)) {
    const label = voiceNoteLabel(attachment);
    return (
      <SenderBubble {...(onMenu ? { onMenu } : {})} quoteText={label}>
        <AudioBubble
          src={attachmentUrl(attachment.attachment_id)}
          durationMs={attachment.duration_ms}
          label={label}
        />
        <Transcript text={attachment.transcript} />
      </SenderBubble>
    );
  }
  return (
    <SenderBubble {...(onMenu ? { onMenu } : {})} quoteText={attachmentLine(attachment)}>
      <span className="flex items-center gap-2">
        <Document className="h-4 w-4 shrink-0" />
        <span className="min-w-0 truncate">{attachmentLine(attachment)}</span>
      </span>
    </SenderBubble>
  );
}

/** The calm rectangle a dragged file is told it may let go over. */
export function DropOverlay() {
  return (
    <div
      data-testid="drop-overlay"
      className="pointer-events-none absolute inset-0 z-30 flex flex-col items-center justify-center
                 gap-2 rounded-2xl border-2 border-dashed border-accent/50 bg-bg/85"
    >
      <p className="text-[15px] text-text">{DROP_COPY}</p>
      <p className="text-xs text-faint">{DROP_TYPES}</p>
    </div>
  );
}

/** A drag carrying files, as opposed to a drag carrying selected text. */
function carriesFiles(event: DragEvent): boolean {
  const types = event.dataTransfer?.types;
  if (!types) return false;
  return Array.from(types as ArrayLike<string>).includes("Files");
}

export type FileDrop = {
  dragging: boolean;
  handlers: {
    onDragEnter: (event: DragEvent) => void;
    onDragOver: (event: DragEvent) => void;
    onDragLeave: (event: DragEvent) => void;
    onDrop: (event: DragEvent) => void;
  };
};

/**
 * Dragging a file over an area, counted rather than watched.
 *
 * `dragenter` and `dragleave` both fire as the pointer crosses every child in
 * the column, so the naive "enter shows, leave hides" flickers the overlay off
 * the moment the file passes over a bubble. A depth counter is the fix: one in
 * per enter, one out per leave, and the overlay stands while the count is
 * positive. `drop` resets it outright, because no leave follows a drop.
 */
export function useFileDrop({
  onFile,
  enabled = true,
}: {
  onFile: (file: File) => void;
  enabled?: boolean;
}): FileDrop {
  const depth = useRef(0);
  const [dragging, setDragging] = useState(false);

  const reset = useCallback(() => {
    depth.current = 0;
    setDragging(false);
  }, []);

  return {
    dragging,
    handlers: {
      onDragEnter: (event) => {
        if (!enabled || !carriesFiles(event)) return;
        event.preventDefault();
        depth.current += 1;
        setDragging(true);
      },
      // Without a prevented dragover the browser refuses the drop outright.
      onDragOver: (event) => {
        if (!enabled || !carriesFiles(event)) return;
        event.preventDefault();
      },
      onDragLeave: (event) => {
        if (!enabled || !carriesFiles(event)) return;
        event.preventDefault();
        depth.current -= 1;
        if (depth.current <= 0) reset();
      },
      onDrop: (event) => {
        if (!enabled || !carriesFiles(event)) return;
        event.preventDefault();
        reset();
        const file = event.dataTransfer?.files?.[0];
        if (file) onFile(file);
      },
    },
  };
}
